import React from 'react'
import { createNgReactBridgeDirective } from '../../react-bridge'
import { DeleteAction } from '../../../components/vulnerabilities-grid/actions/delete-action'
import { Role } from '@contrast-security-inc/base-types'
import {
  PrefixedRole,
  transformRole
} from '../../../graphql/schema/queries/helpers/role'
import { JobTracker } from '../../../components/job-tracker'
import { PureQueryOptions } from 'apollo-client'
import { RouteContext } from '../../../components/route-provider'
import {
  getApplicationDrilldownID,
  getApplicationVulnerabilitiesRoute,
  getServerVulnerabilitiesDrilldownRoute,
  getServerDrilldownID,
  getVulnerabilitiesRoute,
  updateRoute
} from '../../../components/route'

export interface Props {
  readonly vulnerabilities: Array<{
    readonly uuid: string
    readonly application: {
      readonly role: Role
    }
  }>
  readonly refetchQueries: PureQueryOptions[]
  readonly routeParameters: string[]
}

const getRedirectPath = (
  organizationID: string,
  serverId?: string,
  applicationID?: string
) => {
  if (serverId) {
    return getServerVulnerabilitiesDrilldownRoute(organizationID, serverId)
  }

  if (applicationID) {
    return getApplicationVulnerabilitiesRoute(organizationID, applicationID)
  }

  return getVulnerabilitiesRoute(organizationID)
}

const formatApplication = (application, applicationRoles: PrefixedRole[]) => ({
  ...application,
  role: transformRole(applicationRoles) as Role
})

const mapVulnerabilities = (vulnerabilities) =>
  vulnerabilities.map((vulnerability) => ({
    ...vulnerability,
    id: vulnerability.uuid,
    application: formatApplication(
      vulnerability.application,
      vulnerability.application.roles
    )
  }))

export const mapProps = ({ routeParameters, vulnerabilities }: Props) => {
  const applicationID = getApplicationDrilldownID(routeParameters)
  const organizationID = routeParameters[0]
  const serverID = getServerDrilldownID(routeParameters)

  if (!Array.isArray(vulnerabilities)) {
    throw new Error('vulnerabilities is required and must be an array')
  }

  return {
    organizationID,
    refetchQueries: [],
    refresh: () => {
      updateRoute(getRedirectPath(organizationID, serverID, applicationID))
      return Promise.resolve()
    },
    vulnerabilities: mapVulnerabilities(vulnerabilities)
  }
}

export const BridgeComponent = (props: Props) => (
  <RouteContext.Consumer>
    {({ parameters }) => (
      <JobTracker>
        {({ trackJob }) => (
          <DeleteAction
            {...mapProps({ ...props, routeParameters: parameters })}
            size="md"
            trackJob={trackJob}
          />
        )}
      </JobTracker>
    )}
  </RouteContext.Consumer>
)

export const vulnerabilityDeleteAction = createNgReactBridgeDirective(
  'reactVulnerabilityDeleteAction',
  [],
  BridgeComponent,
  ['vulnerabilities']
)
